import React, { useEffect } from "react";
import { useNavigate, useParams, Navigate as RouterNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Shell from "@/components/Shell";
import Brandmark from "@/components/Brandmark";
import { getLens } from "@/lib/lenses";
import { setActiveLens, setAttribution } from "@/lib/attribution";
import { track } from "@/lib/analytics";

export default function Lens() {
  const nav = useNavigate();
  const { lensId } = useParams();
  const lens = getLens(lensId);

  useEffect(() => {
    if (!lens) return;
    setActiveLens(lens.id);
    setAttribution({ lens: lens.id, entry_context: "signal_lens" });
    track("lens_view", { lens: lens.id });
  }, [lens]);

  if (!lens) return <RouterNavigate to="/start" replace />;

  const begin = () => nav("/navigate");

  return (
    <Shell>
      <div className="min-h-[88vh] px-6 pt-20 pb-16 flex flex-col" data-testid={`lens-${lens.id}`}>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
          <Brandmark size={34} />
        </motion.div>

        <div className="flex-1 flex flex-col justify-center py-6">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.05 }}
            className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#8a6d10]"
          >
            {lens.name}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-3 font-serif-display text-[30px] sm:text-4xl leading-[1.2] text-[#1A1A1A]"
            data-testid="lens-headline"
          >
            {lens.headline}
          </motion.h1>
          <p className="mt-5 text-[15px] leading-relaxed text-[#474747]">{lens.copy}</p>
          <p className="mt-6 font-serif-display text-[19px] leading-snug text-[#1A1A1A]">
            {lens.question}
          </p>
        </div>

        <motion.div
          className="mt-8"
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
        >
          <button
            data-testid="lens-cta"
            onClick={begin}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-6 py-4 text-base font-medium ss-tap ss-focus hover:-translate-y-0.5"
          >
            Check my Signal State <ArrowRight size={18} strokeWidth={1.8} />
          </button>
          <p className="mt-4 text-center text-[12.5px] text-[#6B6B6B]">3 questions · about 60 seconds</p>
        </motion.div>
      </div>
    </Shell>
  );
}
